import { brandsData } from './genericData';

/**
 * Static reference/seed data for the Items mock (`items.ts`) — allowed-value option sets,
 * custom-field definitions, and the lookup lists `generateItem`/`generateCustomFields` draw
 * from. Split out of `items.ts` purely to keep that file under the repo's 800-line cap
 * (`.eslintrc.js` `max-lines`); no behavior change, same values, same shapes.
 */

/** Single-select allowed values for the "Season used" CF (customFieldId 8). */
export const allowedSeasonValues = [
  { id: 26, label: 'AW25', userDefinedId: null, parentId: null },
  { id: 28, label: 'AW26', userDefinedId: null, parentId: null },
  { id: 27, label: 'SS25', userDefinedId: null, parentId: null },
  { id: 25, label: 'SS26', userDefinedId: null, parentId: null },
  { id: 30, label: 'SS27', userDefinedId: null, parentId: null },
];

/** Single-select allowed values for the "Care instruction" CF (customFieldId 10). */
const allowedCareValues = [
  { id: 101, label: 'Machine wash', userDefinedId: null, parentId: null },
  { id: 102, label: 'Hand wash', userDefinedId: null, parentId: null },
  { id: 103, label: 'Dry clean only', userDefinedId: null, parentId: null },
];

/** Hierarchy (NestedAllowedValue) values for the "Product group" CF (customFieldId 12). */
const allowedProductGroupValues = [
  { id: 201, label: 'Apparel', userDefinedId: null, parentId: null },
  { id: 202, label: 'Tops', userDefinedId: null, parentId: 201 },
  { id: 203, label: 'Bottoms', userDefinedId: null, parentId: 201 },
  { id: 204, label: 'Footwear', userDefinedId: null, parentId: null },
];

/**
 * Cross-field mother→child hierarchy pair (DL-61900 / DL-61901), distinct from the
 * self-nested "Product group" CF above: two separate custom fields, the child's
 * definition carrying `parentCustomFieldId` pointing at the mother, and the child's
 * allowed values keyed to the mother's via `parentId`.
 *
 * Mother — "Color family" (customFieldId 14) — flat, top-level options.
 */
export const allowedColorFamilyValues = [
  { id: 501, label: 'Warm', userDefinedId: null, parentId: null },
  { id: 502, label: 'Cool', userDefinedId: null, parentId: null },
  { id: 503, label: 'Neutral', userDefinedId: null, parentId: null },
];

/** Child — "Color shade" (customFieldId 15) — options filtered by the mother's selected value. */
const allowedColorShadeValues = [
  { id: 601, label: 'Red', userDefinedId: null, parentId: 501 }, // Warm
  { id: 602, label: 'Orange', userDefinedId: null, parentId: 501 }, // Warm
  { id: 603, label: 'Blue', userDefinedId: null, parentId: 502 }, // Cool
  { id: 604, label: 'Teal', userDefinedId: null, parentId: 502 }, // Cool
  { id: 605, label: 'Beige', userDefinedId: null, parentId: 503 }, // Neutral
  { id: 606, label: 'Grey', userDefinedId: null, parentId: 503 }, // Neutral
];

/**
 * Single-select allowed values for the "Fabric composition" CF (customFieldId 16) — the ONLY
 * `isMandatory: true` custom field definition in this mock, added for the DL-63887 epic ("D2
 * Playwright automation — dl-60144 Clear option in Items inline-edit dropdowns"). Every other
 * CF above is `isMandatory: false`, so there was no existing single-select field whose Clear
 * entry is suppressed by the "mandatory for export" rule (`makeAllowedValueDropdownCell`'s
 * `isRequired = isMandatory && row.readyForExport`) — this is additive, does not change any
 * existing field's behaviour.
 */
export const allowedFabricCompositionValues = [
  { id: 1601, label: 'Cotton', userDefinedId: null, parentId: null },
  { id: 1602, label: 'Polyester', userDefinedId: null, parentId: null },
  { id: 1603, label: 'Wool', userDefinedId: null, parentId: null },
];

/**
 * Matches `CustomFieldDefinition`. Covers every displayable `CustomFieldType`
 * (`frontend/src/features/misc/table/components/customFields/types.ts`) so the column
 * selector offers, and the table can render, one column per type:
 * - 8  `AllowedValue` + `isMultipleAllowedValue` → multiselect (`cf_8_multiselect`)
 * - 9  `Text`                                    → text        (`cf_9_text`)
 * - 10 `AllowedValue` single                     → dropdown    (`cf_10_dd`)
 * - 11 `Date`                                    → date        (`cf_11_date`)
 * - 12 `NestedAllowedValue` (hierarchy)          → dropdown    (`cf_12_dd`)
 * - 13 `Numeric`                                 → number      (`cf_13_number`)
 * Ids 8/9 are unchanged so the existing seeded item CF values (customFieldId 8) and the
 * sibling specs keep working; 10–13 are additive.
 */
export const itemCustomFieldDefinitions = [
  {
    customFieldId: 8,
    name: 'Season used',
    userDefinedId: 'SEASON',
    type: 0,
    isMandatory: false,
    isSupplierEditable: true,
    internalOnly: false,
    maxChar: null,
    isMultiLine: true,
    allowedDecimals: 0,
    isMultipleAllowedValue: true,
    allowedValues: allowedSeasonValues,
    parentCustomFieldId: null,
    parentCustomFieldName: null,
  },
  {
    customFieldId: 9,
    name: 'Internal notes',
    userDefinedId: 'NOTES',
    type: 2,
    isMandatory: false,
    isSupplierEditable: false,
    internalOnly: true,
    maxChar: 500,
    isMultiLine: true,
    allowedDecimals: 0,
    isMultipleAllowedValue: false,
    parentCustomFieldId: null,
    parentCustomFieldName: null,
  },
  {
    customFieldId: 10,
    name: 'Care instruction',
    userDefinedId: 'CARE',
    type: 0,
    isMandatory: false,
    isSupplierEditable: true,
    internalOnly: false,
    maxChar: null,
    isMultiLine: false,
    allowedDecimals: 0,
    isMultipleAllowedValue: false,
    allowedValues: allowedCareValues,
    parentCustomFieldId: null,
    parentCustomFieldName: null,
  },
  {
    customFieldId: 11,
    name: 'Launch date',
    userDefinedId: 'LAUNCH',
    type: 1,
    isMandatory: false,
    isSupplierEditable: true,
    internalOnly: false,
    maxChar: null,
    isMultiLine: false,
    allowedDecimals: 0,
    isMultipleAllowedValue: false,
    parentCustomFieldId: null,
    parentCustomFieldName: null,
  },
  {
    customFieldId: 12,
    name: 'Product group',
    userDefinedId: 'PGROUP',
    type: 3,
    isMandatory: false,
    isSupplierEditable: true,
    internalOnly: false,
    maxChar: null,
    isMultiLine: false,
    allowedDecimals: 0,
    isMultipleAllowedValue: false,
    allowedValues: allowedProductGroupValues,
    parentCustomFieldId: null,
    parentCustomFieldName: null,
  },
  {
    customFieldId: 13,
    name: 'Target weight',
    userDefinedId: 'WEIGHT',
    type: 4,
    isMandatory: false,
    isSupplierEditable: true,
    internalOnly: false,
    maxChar: null,
    isMultiLine: false,
    allowedDecimals: 2,
    isMultipleAllowedValue: false,
    parentCustomFieldId: null,
    parentCustomFieldName: null,
  },
  // Cross-field mother→child hierarchy pair (DL-61900 / DL-61901) — additive, see
  // allowedColorFamilyValues / allowedColorShadeValues above.
  {
    customFieldId: 14,
    name: 'Color family',
    userDefinedId: 'COLORFAMILY',
    type: 3,
    isMandatory: false,
    isSupplierEditable: true,
    internalOnly: false,
    maxChar: null,
    isMultiLine: false,
    allowedDecimals: 0,
    isMultipleAllowedValue: false,
    allowedValues: allowedColorFamilyValues,
    parentCustomFieldId: null,
    parentCustomFieldName: null,
  },
  {
    customFieldId: 15,
    name: 'Color shade',
    userDefinedId: 'COLORSHADE',
    type: 3,
    isMandatory: false,
    isSupplierEditable: true,
    internalOnly: false,
    maxChar: null,
    isMultiLine: false,
    allowedDecimals: 0,
    isMultipleAllowedValue: false,
    allowedValues: allowedColorShadeValues,
    parentCustomFieldId: 14,
    parentCustomFieldName: 'Color family',
  },
  // "Mandatory for export" single-select (DL-63887 epic) — see allowedFabricCompositionValues
  // above. isMandatory: true, but the Clear-suppression rule (`makeAllowedValueDropdownCell`)
  // only kicks in when the OWNING ROW also has `readyForExport: true` — a row that is not (yet)
  // ready for export still offers Clear on this field, by design (a "mandatory for export" field
  // is optional in general, required only before export).
  {
    customFieldId: 16,
    name: 'Fabric composition',
    userDefinedId: 'FABRICCOMP',
    type: 0,
    isMandatory: true,
    isSupplierEditable: true,
    internalOnly: false,
    maxChar: null,
    isMultiLine: false,
    allowedDecimals: 0,
    isMultipleAllowedValue: false,
    allowedValues: allowedFabricCompositionValues,
    parentCustomFieldId: null,
    parentCustomFieldName: null,
  },
];

/**
 * Brands the items are assigned to. Derived **directly** from the same `brandsData`
 * that backs the brand-filter combobox (`GET /api/brands` in genericData), so items and
 * the combobox can never diverge and the brand filter (matched by `id`) always resolves
 * to seeded items. `brandsData` guarantees unique ids at the source (see genericData.ts),
 * so no local dedup is needed. There is deliberately no static fallback: injecting brand
 * ids that `/api/brands` does not return would let the combobox offer a brand no seeded
 * item uses (an always-empty filter). genericData seeds 3–8 brands.
 */
export const brandOptions: { id: number; name: string }[] = brandsData.map(b => ({
  id: b.id,
  name: b.name,
}));

export const organizations = [
  { id: 39, name: 'Delogue Garments' },
  { id: 40, name: 'Delogue Accessories' },
  { id: 41, name: 'Delogue Footwear' },
];

export const suppliers = [
  { id: 44, name: 'Ningbo garment supplier' },
  { id: 45, name: 'Shanghai accessories co.' },
  { id: 46, name: 'Guangzhou textile factory' },
  { id: 47, name: 'Shenzhen packaging ltd.' },
  { id: 48, name: 'Hong Kong labels co.' },
];

export const adminUnits = [
  { id: 19, name: 'pcs', isFraction: false },
  { id: 20, name: 'kg', isFraction: true },
  { id: 21, name: 'm', isFraction: true },
  { id: 22, name: 'set', isFraction: false },
];

export const categories = [
  { id: 87, name: 'Packaging' },
  { id: 88, name: 'Labels' },
  { id: 89, name: 'Tags' },
  { id: 90, name: 'Trims' },
  { id: 91, name: 'Fabric' },
  { id: 92, name: 'Accessories' },
  { id: 93, name: 'Hardware' },
];

export const contactPersons = [
  { id: 'd47e406b-d5db-4787-ade6-7225780b5b5c', name: 'Sara Johnson' },
  { id: 'a12b3c4d-e5f6-7890-abcd-ef1234567890', name: 'Mike Chen' },
  { id: 'b23c4d5e-f6a7-8901-bcde-f12345678901', name: 'Anna Larsen' },
  { id: 'c34d5e6f-a7b8-9012-cdef-123456789012', name: 'James Park' },
];

export const itemNameParts = [
  'Box',
  'Label',
  'Tag',
  'Button',
  'Zipper',
  'Patch',
  'Ribbon',
  'Thread',
  'Lining',
  'Interlining',
  'Buckle',
  'Hook',
  'Snap',
  'Velcro',
  'Elastic',
  'Cord',
  'Bead',
  'Sequin',
  'Stud',
  'Rivet',
  'Badge',
  'Emblem',
  'Tape',
  'Padding',
  'Shoulder pad',
  'Collar stay',
  'Eyelet',
  'Grommet',
  'Drawstring',
];

/**
 * Descriptor suffix — includes both `for shirts` (lowercase) and `Shirt panel`
 * (mixed case) so the case-insensitive text/search spec (DL-62002) can prove
 * partial, case-insensitive matching against deterministic "shirt" occurrences.
 */
export const itemDescriptors = [
  'for garments',
  'for jackets',
  'for trousers',
  'for shirts',
  'Shirt panel',
  'for dresses',
  'for outerwear',
  'for knitwear',
  'for accessories',
  'woven',
  'printed',
  'metal',
  'plastic',
  'rubber',
  'cotton',
  'polyester',
  'nylon',
  'recycled',
];
